import type { CampaignCreative } from "../../../../shared/types/campaign-card.type";

type Props = {
	creatives: CampaignCreative[];
};

const TYPE_LABELS: Record<string, string> = {
	HEADLINE: "Headline",
	DESCRIPTION: "Desc",
	IMAGE: "Image",
	VIDEO: "Video",
	YOUTUBE_VIDEO: "Video",
	TEXT: "Text",
};

function isHttpUrl(value: string): boolean {
	try {
		const u = new URL(value);
		return u.protocol === "http:" || u.protocol === "https:";
	} catch {
		return false;
	}
}

function getOpenHref(creative: CampaignCreative): string | undefined {
	if (!isHttpUrl(creative.content)) return undefined;
	const t = creative.type.toUpperCase();
	if (["IMAGE", "VIDEO", "YOUTUBE_VIDEO", "YOUTUBE"].includes(t)) {
		return creative.content;
	}
	return undefined;
}

function formatLinkLabel(url: string): string {
	try {
		const u = new URL(url);
		const last = u.pathname.split("/").filter(Boolean).pop();
		if (last) return decodeURIComponent(last);
		return u.hostname + u.pathname;
	} catch {
		return url;
	}
}

function isTextType(type: string): boolean {
	const t = type.toUpperCase();
	return t === "HEADLINE" || t === "DESCRIPTION" || t === "TEXT";
}

function isImageType(type: string): boolean {
	return type.toUpperCase() === "IMAGE";
}

function isVideoType(type: string): boolean {
	const t = type.toUpperCase();
	return t === "VIDEO" || t === "YOUTUBE_VIDEO" || t === "YOUTUBE";
}

export const CreativesTable = ({ creatives }: Props) => {
	if (!creatives.length) {
		return (
			<div className='campaign-detail__subheading campaign-detail__creatives-empty'>
				Немає креативів
			</div>
		);
	}

	return (
		<div className='campaign-detail__creatives'>
			<div className='campaign-detail__creatives-head'>
				<div />
				<div>Type</div>
				<div>Content</div>
				<div className='campaign-detail__creatives-open-col'>Open</div>
			</div>
			{creatives.map((c) => (
				<CreativeRow
					key={`${c.adId ?? "ad"}-${c.id ?? c.type}-${c.type}-${c.content}`}
					creative={c}
				/>
			))}
		</div>
	);
};

function CreativeRow({ creative }: { creative: CampaignCreative }) {
	const href = getOpenHref(creative);
	const typeLabel =
		TYPE_LABELS[creative.type.toUpperCase()] ??
		creative.type.replace(/_/g, " ");

	return (
		<div className='campaign-detail__creatives-row'>
			<div className='campaign-detail__creative-icon' aria-hidden>
				<CreativeTypeIcon type={creative.type} />
			</div>
			<div className='campaign-detail__creative-type'>{typeLabel}</div>
			<CreativeContentCell creative={creative} href={href} />
			<div className='campaign-detail__creatives-open-col'>
				{href ? (
					<a
						href={href}
						target='_blank'
						rel='noopener noreferrer'
						className='campaign-detail__creative-open'
						title={
							isVideoType(creative.type)
								? "Відкрити відео"
								: "Відкрити зображення"
						}>
						<img src='/img/icons/link.png' alt='' />
					</a>
				) : (
					<span className='campaign-detail__creative-open-empty'>—</span>
				)}
			</div>
		</div>
	);
}

function CreativeContentCell({
	creative,
	href,
}: {
	creative: CampaignCreative;
	href?: string;
}) {
	if (isTextType(creative.type)) {
		return (
			<div className='campaign-detail__creative-text' title={creative.content}>
				{creative.content}
			</div>
		);
	}

	if (href && isImageType(creative.type)) {
		return (
			<a
				href={href}
				target='_blank'
				rel='noopener noreferrer'
				className='campaign-detail__creative-link'>
				<img
					src={href}
					alt=''
					className='campaign-detail__creative-thumb'
					loading='lazy'
					onError={(e) => {
						e.currentTarget.style.display = "none";
					}}
				/>
				<span className='campaign-detail__creative-link-label'>
					{formatLinkLabel(href)}
				</span>
			</a>
		);
	}

	if (href) {
		return (
			<a
				href={href}
				target='_blank'
				rel='noopener noreferrer'
				className='campaign-detail__creative-link campaign-detail__creative-link--url'
				title={creative.content}>
				<span className='campaign-detail__creative-link-label'>
					{formatLinkLabel(href)}
				</span>
			</a>
		);
	}

	return (
		<div className='campaign-detail__creative-text' title={creative.content}>
			{creative.content}
		</div>
	);
}

function CreativeTypeIcon({ type }: { type: string }) {
	const t = type.toUpperCase();
	if (isImageType(t)) {
		return (
			<svg viewBox='0 0 16 16' width='14' height='14' fill='currentColor'>
				<path d='M2 3a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V3zm2 1v6.5l2.5-2.5 2 2L11 7l3 3V4H4zm1.5 1a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z' />
			</svg>
		);
	}
	if (isVideoType(t)) {
		return (
			<svg viewBox='0 0 16 16' width='14' height='14' fill='currentColor'>
				<path d='M3 3h7l3 2v6l-3 2H3V3zm2 2v6h4.2L11 10V6l-1.8-1H5z' />
			</svg>
		);
	}
	if (t === "HEADLINE") {
		return (
			<svg viewBox='0 0 16 16' width='14' height='14' fill='currentColor'>
				<path d='M3 4h10v1.5H9.5V12H8V5.5H3V4zm0 8h10v1.5H3V12z' />
			</svg>
		);
	}
	if (t === "DESCRIPTION") {
		return (
			<svg viewBox='0 0 16 16' width='14' height='14' fill='currentColor'>
				<path d='M3 3h10v1.5H3V3zm0 3h10v1.5H3V6zm0 3h7v1.5H3V9zm0 3h10v1.5H3V12z' />
			</svg>
		);
	}
	return (
		<svg viewBox='0 0 16 16' width='14' height='14' fill='currentColor'>
			<path d='M3 4h10v1H3V4zm0 3h10v1H3V7zm0 3h7v1H3v-1z' />
		</svg>
	);
}
